// src/components/workout/InitialWeightsDialog.jsx
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ScrollArea } from "../ui/scroll-area";
import { supabase } from '../../lib/supabase';
import { Dumbbell } from "lucide-react";

/**
 * Dialog for setting initial weights for exercises
 * Used both for first-time setup and later adjustments
 */
const InitialWeightsDialog = ({ 
  open, 
  onOpenChange, 
  userId, 
  exercises, 
  currentWeights = {}, 
  unitSystem = 'imperial',
  onWeightsUpdated
}) => {
  // In InitialWeightsDialog.jsx
const [weights, setWeights] = useState(() => {
  // Log what we received
  console.log('Initializing weights dialog with currentWeights:', currentWeights);
  console.log('Exercises to display:', exercises.map(e => e.name));
  
  // Initialize with current weights or empty object
  const initialWeights = {};
  exercises.forEach(exercise => {
    // First check for exact match
    if (currentWeights[exercise.name] !== undefined) {
      initialWeights[exercise.name] = currentWeights[exercise.name];
      return;
    }
    
    // Then check for partial matches (e.g., "Squat" vs "Back Squat")
    const matchingExercise = Object.keys(currentWeights).find(key => 
      key.includes(exercise.name) || exercise.name.includes(key)
    );
    
    if (matchingExercise) {
      initialWeights[exercise.name] = currentWeights[matchingExercise];
    } else {
      // Default to empty string if no match found
      initialWeights[exercise.name] = '';
    }
  });
  
  console.log('Final initialized weights:', initialWeights);
  return initialWeights;
});
  
  const [loading, setLoading] = useState(false);
  
  const handleSave = async () => {
    console.log('Saving weights for user ID:', userId);
    console.log('Weights being saved:', weights);
    try {
      setLoading(true);
      
      // Create exercise progress entries for each exercise
      const updates = [];
      
      for (const exercise of exercises) {
        // Skip if no weight entered
        if (!weights[exercise.name]) continue;
        
        // Check if there's an existing record for this exercise
        const { data: existing, error: checkError } = await supabase
          .from('exercise_progress')
          .select('id')
          .eq('user_id', userId)
          .eq('exercise_name', exercise.name)
          .single();
        
        if (checkError && checkError.code !== 'PGRST116') {
          throw checkError;
        }
        
        if (existing) {
          // Update existing record
          updates.push(
            supabase
              .from('exercise_progress')
              .update({
                current_weight: parseFloat(weights[exercise.name]),
                fail_count: 0,
                updated_at: new Date().toISOString()
              })
              .eq('id', existing.id)
          );
        } else {
          // Insert new record
          updates.push(
            supabase
              .from('exercise_progress')
              .insert({
                user_id: userId,
                exercise_name: exercise.name,
                current_weight: parseFloat(weights[exercise.name]),
                fail_count: 0
              })
          );
        }
      }
      
      // Execute all updates in parallel
      await Promise.all(updates.map(query => query));
      
      // Call the callback with the updated weights
      if (onWeightsUpdated) {
        const updatedWeights = {};
        Object.entries(weights).forEach(([exercise, weight]) => {
          if (weight) {
            updatedWeights[exercise] = parseFloat(weight);
          }
        });
        onWeightsUpdated(updatedWeights);
      }
      
      // Close the dialog
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving initial weights:', error);
      // Handle error (could add toast notification here)
    } finally {
      setLoading(false);
    }
  };

  const resetAllWeights = () => {
    const emptyWeights = {};
    exercises.forEach(exercise => {
      emptyWeights[exercise.name] = '';
    });
    setWeights(emptyWeights);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Dumbbell className="h-5 w-5" />
            Set Working Weights
          </DialogTitle>
          <DialogDescription>
            Enter your starting weight for each exercise. These will be your working weights for your first workout.
            <br></br>
            If you do not know what weight to use, you can enter 0 and change it once you're in the workout or from the workout settings.
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-end mb-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={resetAllWeights}
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            Reset All Weights
          </Button>
        </div>
        
        <ScrollArea className="max-h-[60vh] pr-3">
          <div className="space-y-4 py-3">
            {exercises.map((exercise) => (
              <div key={exercise.name} className="space-y-2">
                <Label htmlFor={`weight-${exercise.name}`}>{exercise.name}</Label>
                <div className="flex items-center space-x-2">
                <Input
                  id={`weight-${exercise.name}`}
                  type="number"
                  placeholder={`Enter weight`}
                  value={weights[exercise.name] !== undefined ? weights[exercise.name].toString() : ''}
                  onChange={(e) => {
                    const newWeights = { ...weights };
                    newWeights[exercise.name] = e.target.value;
                    console.log(`Changed ${exercise.name} weight to:`, e.target.value);
                    setWeights(newWeights);
                  }}
                />
                  <span className="text-sm">{unitSystem === 'imperial' ? 'lbs' : 'kg'}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {exercise.notes || (
                    exercise.name === 'Squat' ? 'Recommended to start with just the bar (45lbs/20kg) if you\'re new' : 
                    exercise.name === 'Deadlift' ? 'Start with 95lbs/40kg if you\'re new to deadlifts' :
                    'Start with a weight you can lift comfortably with good form'
                  )}
                </p>
              </div>
            ))}
          </div>
        </ScrollArea>
        
        <DialogFooter className="mt-6">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Weights'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InitialWeightsDialog;